/**
 * Catálogos estáticos de opciones portados 1:1 del `workflow-node-config-panel` monolítico.
 * Son enums/listas cerradas del contrato del backend (operadores, tipos de destino, unidades…)
 * que NO viven en el catálogo dinámico de nodos, por eso se mantienen aquí como constantes.
 *
 * INC_CRM_020 (8) inc26-46 — TODAS las listas son `{ value, label }`: `value` es la clave EXACTA
 * del contrato del backend (nunca se traduce, se guarda tal cual) y `label` es el texto en
 * español que ve el usuario. Antes eran arrays de strings crudos que las plantillas pintaban
 * con `{{ o }}`, así que el inspector mostraba GREATER_THAN_OR_EQUALS, CURRENT_RECORD, IN_APP…
 *
 * REGLA: nunca añadir aquí una lista de strings sueltos ni pintar `option.value` al usuario.
 */

import { WorkflowPropertyValueKind } from '../../../models/workflow-variable-catalog';

/** Opción de un desplegable del inspector: clave del backend + etiqueta en español. */
export interface WorkflowConfigOption {
  readonly value: string;
  readonly label: string;
}

type Options = ReadonlyArray<WorkflowConfigOption>;

/** Índice `value → label` de una lista de opciones, para buscar la etiqueta de una clave suelta. */
function labelOf(options: Options, value: string, overrides?: Readonly<Record<string, string>>): string {
  return overrides?.[value] ?? options.find((o) => o.value === value)?.label ?? value;
}

/** Operadores de comparación (condiciones, filtros de disparador, esperas, reglas de routing). */
export const CONDITION_OPERATOR_OPTIONS: Options = [
  { value: 'EQUALS', label: 'Es igual a' },
  { value: 'NOT_EQUALS', label: 'Es distinto de' },
  { value: 'CONTAINS', label: 'Contiene' },
  { value: 'NOT_CONTAINS', label: 'No contiene' },
  { value: 'IS_EMPTY', label: 'Está vacío' },
  { value: 'IS_NOT_EMPTY', label: 'No está vacío' },
  { value: 'GREATER_THAN', label: 'Es mayor que' },
  { value: 'LESS_THAN', label: 'Es menor que' },
  { value: 'GREATER_THAN_OR_EQUALS', label: 'Es mayor o igual que' },
  { value: 'LESS_THAN_OR_EQUALS', label: 'Es menor o igual que' },
  { value: 'BETWEEN', label: 'Está entre' },
  { value: 'IN', label: 'Está en la lista' },
  { value: 'NOT_IN', label: 'No está en la lista' }
];

/**
 * Operadores que TIENEN SENTIDO para cada forma de valor (INC_CRM_020 (13) inc52/54/56/57/58/60/61).
 *
 * La lista de arriba es el contrato COMPLETO del backend
 * (`WorkflowConditionEvaluationService.evaluateComparison`), y se ofrecía entera pasara lo que
 * pasara con la propiedad elegida: se podía pedir «Empresa es mayor que…», que el motor resuelve
 * comparando el id numérico de la empresa —una comparación sin significado para nadie— o
 * «Estado está entre…», que sin extremos evalúa SIEMPRE falso y sin ningún aviso. El usuario
 * construía una rama muerta creyendo haberla configurado.
 *
 * Criterio de cada lista:
 * - `TEXT` — nada de orden (`>` sobre texto es comparación lexicográfica, que nadie espera).
 * - `NUMBER` — todo: es la única forma en la que el orden y el rango son literales.
 * - `DATE` — orden y rango sí, pertenencia a lista no (nadie enumera fechas exactas).
 * - `BOOLEAN` — solo igualdad: un Sí/No no contiene, no ordena y no pertenece a listas.
 * - `CATALOG` / `ENTITY` — igualdad y pertenencia: son claves de un conjunto cerrado, no escalares
 *   ordenables ni texto en el que buscar subcadenas.
 *
 * Una propiedad DESCONOCIDA (atributo personalizado, campo del evento, clave escrita a mano) no
 * tiene forma conocida → se siguen ofreciendo los trece. Nunca se restringe lo que no se entiende.
 */
const OPERATORS_BY_KIND: Readonly<Record<WorkflowPropertyValueKind, readonly string[]>> = {
  TEXT: ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'NOT_CONTAINS', 'IS_EMPTY', 'IS_NOT_EMPTY', 'IN', 'NOT_IN'],
  NUMBER: [
    'EQUALS', 'NOT_EQUALS', 'GREATER_THAN', 'LESS_THAN', 'GREATER_THAN_OR_EQUALS',
    'LESS_THAN_OR_EQUALS', 'BETWEEN', 'IS_EMPTY', 'IS_NOT_EMPTY', 'IN', 'NOT_IN'
  ],
  DATE: [
    'EQUALS', 'NOT_EQUALS', 'GREATER_THAN', 'LESS_THAN', 'GREATER_THAN_OR_EQUALS',
    'LESS_THAN_OR_EQUALS', 'BETWEEN', 'IS_EMPTY', 'IS_NOT_EMPTY'
  ],
  BOOLEAN: ['EQUALS', 'NOT_EQUALS', 'IS_EMPTY', 'IS_NOT_EMPTY'],
  CATALOG: ['EQUALS', 'NOT_EQUALS', 'IN', 'NOT_IN', 'IS_EMPTY', 'IS_NOT_EMPTY'],
  ENTITY: ['EQUALS', 'NOT_EQUALS', 'IN', 'NOT_IN', 'IS_EMPTY', 'IS_NOT_EMPTY']
};

/**
 * Etiquetas que cambian según la forma del valor. La CLAVE que se guarda es siempre la misma
 * (`GREATER_THAN`); solo cambia cómo se lee: «es mayor que» describe un monto, no una fecha.
 */
const OPERATOR_LABEL_OVERRIDES: Partial<Record<WorkflowPropertyValueKind, Readonly<Record<string, string>>>> = {
  DATE: {
    GREATER_THAN: 'Es posterior a',
    LESS_THAN: 'Es anterior a',
    GREATER_THAN_OR_EQUALS: 'Es igual o posterior a',
    LESS_THAN_OR_EQUALS: 'Es igual o anterior a',
    BETWEEN: 'Está entre las fechas',
    IS_EMPTY: 'Está vacía',
    IS_NOT_EMPTY: 'No está vacía'
  },
  CATALOG: {
    IN: 'Es alguno de',
    NOT_IN: 'No es ninguno de'
  },
  ENTITY: {
    IN: 'Es alguno de',
    NOT_IN: 'No es ninguno de'
  },
  BOOLEAN: {
    EQUALS: 'Es',
    NOT_EQUALS: 'No es'
  }
};

/**
 * Las listas resultantes se cachean por `kind|operador actual`: el `<select>` de operador se
 * repinta en cada ciclo de detección de cambios y devolver un array nuevo cada vez rompería el
 * `track` de `@for` en las filas de IF_ELSE / MULTI_BRANCH / filtros.
 */
const operatorOptionsCache = new Map<string, Options>();

/**
 * Operadores ofrecidos para una propiedad concreta.
 *
 * `current` es el operador YA GUARDADO en el nodo: si no encaja con la forma de la propiedad
 * (porque el nodo se configuró antes, o porque el usuario acaba de cambiar la propiedad) se
 * conserva en la lista marcado como no aplicable, en lugar de desaparecer del desplegable. Ocultarlo
 * dejaría el `<select>` mostrando otra cosa distinta de lo que el nodo ejecuta realmente.
 */
export function conditionOperatorOptionsFor(
  kind: WorkflowPropertyValueKind | undefined,
  current?: string | null
): Options {
  const allowed = kind ? OPERATORS_BY_KIND[kind] : undefined;
  if (!allowed) {
    return CONDITION_OPERATOR_OPTIONS;
  }
  const saved = (current ?? '').trim().toUpperCase();
  const cacheKey = `${kind}|${allowed.includes(saved) ? '' : saved}`;
  const cached = operatorOptionsCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const overrides = kind ? OPERATOR_LABEL_OVERRIDES[kind] : undefined;
  const options: WorkflowConfigOption[] = allowed.map((value) => ({
    value,
    label: labelOf(CONDITION_OPERATOR_OPTIONS, value, overrides)
  }));
  if (saved !== '' && !allowed.includes(saved)) {
    options.push({
      value: saved,
      label: `${labelOf(CONDITION_OPERATOR_OPTIONS, saved)} (no aplica a esta propiedad)`
    });
  }
  const frozen: Options = options;
  operatorOptionsCache.set(cacheKey, frozen);
  return frozen;
}

/**
 * De dónde sale el valor que se compara en una condición.
 *
 * `OBJECT` va primero porque es el caso normal —comparar una propiedad del registro inscrito— y
 * es el que corresponde a las propiedades que ofrece el selector (`email`, `contactCycleName`…).
 * NO usar `PAYLOAD` para eso: ahí los campos viajan con el nombre de la API (`mail`, `fullname`),
 * nunca casan con los del selector y la condición da siempre falso sin avisar.
 */
/**
 * Propiedades que el CRM publica hoy como CAMBIO ({@code property}/{@code oldValue}/
 * {@code newValue}) para cada tipo de objeto, que son las únicas con las que el disparador
 * PROPERTY_CHANGED puede casar: el evaluador compara la propiedad configurada contra
 * `payload.property`, así que elegir cualquier otra deja un flujo que no arranca nunca.
 *
 * Emisores: `ContactLifecycleEventPublisher` (ciclo y etapa), `DealService` (etapa del negocio) y
 * `TicketService` (estado y prioridad). Empresas y cotizaciones no publican ningún cambio todavía.
 * Al añadir uno nuevo en el backend, añadir aquí su clave.
 */
export const PROPERTY_CHANGED_SUPPORTED_KEYS: Readonly<Record<string, readonly string[]>> = {
  CONTACT: ['idContactCycle', 'idContactState'],
  DEAL: ['idDealCycle'],
  TICKET: ['idTicketStatus', 'idTicketPriority'],
  COMPANY: [],
  QUOTE: []
};

export const CONDITION_SOURCE_OPTIONS: Options = [
  { value: 'OBJECT', label: 'Propiedad del registro' },
  { value: 'PAYLOAD', label: 'Datos del evento' },
  { value: 'CONTEXT', label: 'Contexto del workflow' },
  { value: 'STEP_OUTPUT', label: 'Resultado del paso anterior' }
];

export const WAIT_UNIT_OPTIONS: Options = [
  { value: 'MINUTES', label: 'Minutos' },
  { value: 'HOURS', label: 'Horas' },
  { value: 'DAYS', label: 'Días' },
  { value: 'WEEKS', label: 'Semanas' }
];

/**
 * Zona por defecto de las horas que se escriben en un nodo (vencimiento de tarea, inicio de
 * reunión). DEBE coincidir con `WorkflowTaskActivityActionService.DEFAULT_TIMEZONE` del backend:
 * es el valor que este aplica cuando la config no trae zona, y enseñar otro distinto fue justo
 * el origen del desfase silencioso de 5 horas.
 */
export const DEFAULT_WORKFLOW_TIMEZONE = 'America/Lima';

/** Identificadores IANA ofrecidos para fechas de nodos. Son valores crudos: no se traducen. */
export const WORKFLOW_TIMEZONE_OPTIONS: readonly string[] = [
  'America/Lima',
  'America/Bogota',
  'America/Mexico_City',
  'America/Santiago',
  'America/Buenos_Aires',
  'America/Caracas',
  'America/La_Paz',
  'America/Asuncion',
  'America/Montevideo',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Europe/Madrid',
  'Europe/London',
  'UTC'
];

/**
 * Prioridades de tarea POR DEFECTO. El `value` viaja tal cual y el backend
 * (`WorkflowTaskActivityActionService.resolveTaskPriorityId`) lo empareja contra el NOMBRE del
 * catálogo del negocio → no se puede traducir el value, solo la etiqueta.
 */
export const PRIORITY_OPTIONS: Options = [
  { value: 'LOW', label: 'Baja' },
  { value: 'MEDIUM', label: 'Media' },
  { value: 'HIGH', label: 'Alta' },
  { value: 'URGENT', label: 'Urgente' }
];

/**
 * Tipos de actividad de la condición «Historial de actividad».
 *
 * El motor los compara contra el historial que devuelve el módulo de Auditoría: usa el SUBTIPO de
 * la operación cuando lo hay (`SEND_EMAIL`, `CHANGE_CYCLE`…) y, si no, el nombre de la entidad
 * tocada (`MEETING`, `TICKET`…). Por eso conviven los dos formatos en esta lista: son las claves
 * reales que hay en `audit_log`, no una invención.
 */
export const ACTIVITY_TYPE_OPTIONS: Options = [
  { value: 'MEETING', label: 'Tuvo una reunión' },
  { value: 'TASK', label: 'Tuvo una tarea' },
  { value: 'TICKET', label: 'Tuvo un ticket' },
  { value: 'QUOTE', label: 'Tuvo una cotización' },
  { value: 'SEND_EMAIL', label: 'Se le envió un correo' },
  { value: 'CHANGE_CYCLE', label: 'Cambió de ciclo de vida' },
  { value: 'PUBLIC_LINK_VIEWED', label: 'Vio una cotización compartida' }
];

export const CRM_OBJECT_TYPE_OPTIONS: Options = [
  { value: 'CONTACT', label: 'Contacto' },
  { value: 'COMPANY', label: 'Empresa' },
  { value: 'DEAL', label: 'Negocio' },
  { value: 'QUOTE', label: 'Cotización' }
];

/**
 * Tipos de registro que acepta la ACCIÓN `CREATE_RECORD` (`WorkflowCrmActionService`).
 *
 * Incluye LEAD, que NO está en `CRM_OBJECT_TYPE_OPTIONS` a propósito: ese enum es el de
 * `WorkflowObjectType` (de qué objeto puede SER un workflow: inscripciones, disparadores,
 * registro actual) y los leads no lo soportan. En `CREATE_RECORD` el tipo viaja como texto
 * en la config, así que aquí sí se puede ofrecer.
 */
export const CREATE_RECORD_OBJECT_TYPE_OPTIONS: Options = [
  { value: 'CONTACT', label: 'Contacto' },
  { value: 'COMPANY', label: 'Empresa' },
  { value: 'DEAL', label: 'Negocio' },
  { value: 'LEAD', label: 'Lead' },
  { value: 'QUOTE', label: 'Cotización' }
];

/** Campo del disparador "Cambio de ciclo o etapa" (LIFECYCLE_CHANGED). */
export const LIFECYCLE_FIELD_OPTIONS: Options = [
  { value: 'CYCLE', label: 'Ciclo de vida' },
  { value: 'STATE', label: 'Etapa' }
];

/**
 * Sobre qué registro actúa la acción.
 *
 * Sin `CONTEXT`: `WorkflowCrmActionService.resolveTargetRecord` lo hace caer en el mismo `default`
 * que `EXPLICIT_RECORD` —resuelve por `target.objectType` + `target.objectId`— así que nunca leyó
 * nada del contexto del workflow. Ofrecerlo solo servía para que el usuario creyera haber
 * configurado algo distinto de lo que se ejecuta. Los nodos que ya lo tuvieran guardado se
 * normalizan a EXPLICIT_RECORD al abrirlos (ver `crm-action-config.component.ts`).
 */
export const TARGET_TYPE_OPTIONS: Options = [
  { value: 'CURRENT_RECORD', label: 'El registro inscrito en el workflow' },
  { value: 'EXPLICIT_RECORD', label: 'Un registro concreto que yo elija' }
];

/** De dónde se lee el valor de origen (copiar propiedad, asociar registros). */
export const SOURCE_TYPE_OPTIONS: Options = [
  { value: 'CONTEXT', label: 'Contexto del workflow' },
  { value: 'PAYLOAD', label: 'Datos del evento' },
  { value: 'CURRENT_RECORD', label: 'El registro inscrito en el workflow' },
  { value: 'ASSOCIATED_RECORD', label: 'Un registro asociado' },
  { value: 'EXPLICIT_RECORD', label: 'Un registro concreto que yo elija' }
];

/** Operación de la acción "Asignar propiedad" (SET_PROPERTY_VALUE). */
export const SPV_OPERATION_OPTIONS: Options = [
  { value: 'SET', label: 'Asignar un valor' },
  { value: 'CLEAR', label: 'Vaciar la propiedad' },
  { value: 'COPY_FROM_PROPERTY', label: 'Copiar desde otra propiedad' },
  { value: 'INCREMENT', label: 'Aumentar (sumar)' },
  { value: 'DECREMENT', label: 'Disminuir (restar)' },
  { value: 'FORMULA', label: 'Calcular con una fórmula' }
];

export const LIST_TYPE_OPTIONS: Options = [
  { value: 'CONTACT_SEGMENT', label: 'Lista de contactos' },
  { value: 'COMPANY_SEGMENT', label: 'Lista de empresas' },
  { value: 'DEAL_SEGMENT', label: 'Lista de negocios' }
];

/** Qué registro se inscribe / desinscribe del workflow destino. */
export const ENROLL_OBJECT_ID_SOURCE_OPTIONS: Options = [
  { value: 'CURRENT_RECORD', label: 'El registro inscrito en este workflow' },
  { value: 'EXPLICIT', label: 'Un registro concreto que yo elija' }
];

export const SEGMENT_EVENT_OPTIONS: Options = [
  { value: 'ENTERED', label: 'Entra al segmento' },
  { value: 'EXITED', label: 'Sale del segmento' }
];

// INC_CRM_020 (6) inc19 — aquí vivía EMAIL_INTERACTION_OPTIONS, una lista local en inglés
// (['OPENED','CLICKED','BOUNCED','UNSUBSCRIBED']) que además había derivado de lo que el backend
// publica: 'UNSUBSCRIBED' no existe como evento (la baja llega como queja de spam), así que
// elegirlo construía un workflow que no se disparaba nunca. Las interacciones salen ahora del
// catálogo del backend, igual que el resto de eventos — ver `emailInteractionOptions` en
// trigger-config.component.ts.

/**
 * Sin CUSTOM_OBJECT ni SUBSCRIPTION: no existen como entidad del CRM, así que nunca podrían
 * pertenecer a un segmento. Tampoco TICKET, que sí existe pero no es miembro de segmentos
 * (los segmentos agrupan contactos, empresas y negocios) — el backend lo rechaza con ese motivo.
 */
export const SM_MEMBERSHIP_OBJECT_TYPES: Options = [
  { value: 'CONTACT', label: 'Contacto' },
  { value: 'COMPANY', label: 'Empresa' },
  { value: 'DEAL', label: 'Negocio' },
  { value: 'QUOTE', label: 'Cotización' }
];

/** Destinatario de la notificación interna. */
export const NOTIF_RECIPIENT_TYPE_OPTIONS: Options = [
  { value: 'OWNER', label: 'Responsable del registro' },
  { value: 'USER', label: 'Un usuario concreto' },
  { value: 'EMAIL', label: 'Una dirección de correo' },
  { value: 'TEAM', label: 'Un equipo' }
];

/** Canal por el que llega la notificación interna. */
export const NOTIF_CHANNEL_OPTIONS: Options = [
  { value: 'IN_APP', label: 'Notificación dentro del CRM' },
  { value: 'EMAIL', label: 'Correo electrónico' }
];

/*
 * NO añadir aquí una lista de eventos del CRM.
 *
 * Existía `CRM_EVENT_OPTIONS` (20 claves crudas) y era una copia paralela del catálogo del
 * backend: pintaba `CONTACT_CYCLE_CHANGED` al usuario, ofrecía dos eventos que ningún módulo
 * publica (`SUBSCRIPTION_*`) y no sabía qué eventos aplican al `objectType` del workflow, con lo
 * que dejaba crear workflows que no se disparaban nunca.
 *
 * La fuente única es `GET /api/workflows/{id}/builder/catalog` → `crmEvents` (etiqueta,
 * descripción, grupo y aplicabilidad) + `items[].eventOptions` (claves admitidas por subtipo),
 * expuesto por `WorkflowEditorService.crmEvents()` y consumido en `trigger-config.component.ts`.
 */

/**
 * Tipos de tarea POR DEFECTO. Igual que `PRIORITY_OPTIONS`: el `value` es lo que el backend
 * empareja contra el catálogo del negocio, así que solo se traduce la etiqueta.
 */
export const TASK_TYPE_OPTIONS: Options = [
  { value: 'CALL', label: 'Llamada' },
  { value: 'EMAIL', label: 'Correo' },
  { value: 'MEETING', label: 'Reunión' },
  { value: 'TODO', label: 'Pendiente' },
  { value: 'FOLLOW_UP', label: 'Seguimiento' }
];

/** Método HTTP del webhook saliente. Se conserva la sigla (es el estándar) con su significado. */
export const WEBHOOK_METHOD_OPTIONS: Options = [
  { value: 'GET', label: 'GET — consultar datos' },
  { value: 'POST', label: 'POST — enviar / crear' },
  { value: 'PUT', label: 'PUT — reemplazar' },
  { value: 'PATCH', label: 'PATCH — actualizar' },
  { value: 'DELETE', label: 'DELETE — eliminar' }
];
